import React, { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Form, Input, Modal, Space, Steps, Tag, Typography, message } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../assets/utils/useAuth';
import lib from '../../assets/utils/lib';
import { add_user } from '../../stores/user';
import logoImage from '../../assets/images/icon/image.png';
import {
  getEnvironmentBadge,
  persistRememberedUsername,
  readRememberedUsername,
  resolvePostLoginRoute,
  SESSION_EXPIRED_REASON,
} from './loginUtils';
import './login.css';

const { Text, Title } = Typography;

const LoginPageHook = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [resetForm] = Form.useForm();
  const { set, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [redirectHint, setRedirectHint] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(0);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const sessionExpired = searchParams.get('reason') === SESSION_EXPIRED_REASON;
  const envBadge = getEnvironmentBadge();
  const rememberedUsername = readRememberedUsername();

  useEffect(() => {
    if (rememberedUsername) {
      form.setFieldsValue({ username: rememberedUsername, remember: true });
    }
  }, [form, rememberedUsername]);

  useEffect(() => {
    if (!user) return;
    const { path, label } = resolvePostLoginRoute(user);
    setRedirectHint(`Taking you to ${label}…`);
    const timer = setTimeout(() => navigate(path, { replace: true }), 400);
    return () => clearTimeout(timer);
  }, [navigate, user]);

  const clearSessionExpiredParam = () => {
    if (sessionExpired) {
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const logUserIn = async ({ username, password, remember }) => {
    setLoading(true);
    setFormError('');
    clearSessionExpiredParam();

    try {
      const loginPayload = {
        username: String(username || '').trim(),
        password,
      };

      const reqData = await lib.login(loginPayload);

      if (reqData?.status === 'error' || reqData?.error) {
        setFormError(reqData?.msg || reqData?.message || 'Unable to sign in. Check your credentials and try again.');
        return;
      }

      if (reqData?.data?.error) {
        setFormError(reqData.data.message || 'Invalid email/staff ID or password.');
        return;
      }

      const loginData = reqData?.data?.data || reqData?.data;
      if (!loginData) {
        setFormError('Unexpected response from server. Please try again.');
        return;
      }

      persistRememberedUsername(loginPayload.username, remember);
      dispatch(add_user({ user: loginData }));
      await set(loginData);

      if (loginData.email_missing) {
        message.warning('Your account has no email on file. Ask an admin to add one so you can receive notices and reset your password.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setFormError(
        error?.response?.data?.message
        || error?.message
        || 'An unexpected error occurred. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const openReset = () => {
    setResetStep(0);
    setResetEmail('');
    resetForm.resetFields();
    const current = form.getFieldValue('username');
    if (current && String(current).includes('@')) {
      resetForm.setFieldsValue({ email: String(current).trim() });
    }
    setResetOpen(true);
  };

  const handleRequestReset = async ({ email }) => {
    setResetLoading(true);
    try {
      const resp = await lib.requestStaffPasswordReset(email);
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        message.error(parsed.message || 'Unable to request reset');
        return;
      }
      setResetEmail(String(email).trim());
      message.success(parsed.message || 'If that email exists, a reset code was sent.');
      setResetStep(1);
    } catch (e) {
      message.error(e?.message || 'Unable to request reset');
    } finally {
      setResetLoading(false);
    }
  };

  const handleCompleteReset = async ({ code, password, confirm_password }) => {
    setResetLoading(true);
    try {
      const resp = await lib.completeStaffPasswordReset({
        email: resetEmail,
        code,
        password,
        confirm_password,
      });
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        message.error(parsed.message || 'Unable to reset password');
        return;
      }
      message.success('Password updated. You can sign in now.');
      setResetOpen(false);
      form.setFieldsValue({ username: resetEmail });
    } catch (e) {
      message.error(e?.message || 'Unable to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="care-login-page">
      {envBadge ? (
        <div className="login-env-badge">
          <Tag color={envBadge.color}>{envBadge.label}</Tag>
        </div>
      ) : null}

      <div className="login-shell">
        <section className="login-card">
          <header className="login-card__header">
            <img src={logoImage} alt="Aman HMO" className="login-card__logo" />
            <Title level={3} className="login-card__title">Care Admin</Title>
            <Text type="secondary" className="login-card__subtitle">
              Pre-authorizations, claims, organizations, and enrollee operations in one workspace.
            </Text>
          </header>

          {sessionExpired ? (
            <Alert
              type="warning"
              showIcon
              closable
              onClose={clearSessionExpiredParam}
              className="login-card__alert"
              message="Session expired"
              description="Your session timed out or was no longer valid. Sign in again to continue."
            />
          ) : null}

          {redirectHint ? (
            <Alert type="info" showIcon className="login-card__alert" message={redirectHint} />
          ) : null}

          {formError ? (
            <Alert
              type="error"
              showIcon
              closable
              onClose={() => setFormError('')}
              className="login-card__alert"
              message={formError}
            />
          ) : null}

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={logUserIn}
            initialValues={{ remember: Boolean(rememberedUsername) }}
            className="login-form"
          >
            <Form.Item
              name="username"
              label="Email or Staff ID"
              rules={[{ required: true, message: 'Enter your email or Staff ID' }]}
            >
              <Input
                size="large"
                autoComplete="username"
                placeholder="you@amanhmo.com or staff ID"
                disabled={loading || Boolean(redirectHint)}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Enter your password' }]}
            >
              <Input.Password
                size="large"
                autoComplete="current-password"
                placeholder="Password"
                disabled={loading || Boolean(redirectHint)}
              />
            </Form.Item>

            <div className="login-form__meta">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox disabled={loading || Boolean(redirectHint)}>Remember username</Checkbox>
              </Form.Item>
              <Button
                type="link"
                onClick={openReset}
                disabled={loading || Boolean(redirectHint)}
                style={{ paddingInline: 0 }}
              >
                Forgot password?
              </Button>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              disabled={Boolean(redirectHint)}
              className="login-form__submit"
            >
              Sign in
            </Button>
          </Form>

          <footer className="login-card__footer">
            <Text type="secondary">Powered by Claima Technologies</Text>
            <Text type="secondary">&copy; {new Date().getFullYear()} Aman HMO</Text>
          </footer>
        </section>

        <aside className="login-aside" aria-hidden="true">
          <p className="login-aside__eyebrow">Operations workspace</p>
          <h2>Run care delivery with clarity.</h2>
          <ul>
            <li>Pre-auth and claims queues</li>
            <li>Organization and enrollee management</li>
            <li>Role-based access across teams</li>
          </ul>
        </aside>
      </div>

      <Modal
        title="Reset admin password"
        open={resetOpen}
        onCancel={() => setResetOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Steps
          size="small"
          current={resetStep}
          style={{ marginBottom: 20 }}
          items={[{ title: 'Email' }, { title: 'New password' }]}
        />

        {resetStep === 0 ? (
          <Form layout="vertical" form={resetForm} onFinish={handleRequestReset}>
            <Form.Item
              name="email"
              label="Account email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email' },
              ]}
            >
              <Input placeholder="you@amanhmo.com" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={resetLoading}>
              Send reset code
            </Button>
          </Form>
        ) : (
          <Form layout="vertical" onFinish={handleCompleteReset}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Code sent to ${resetEmail} (if the account exists).`}
            />
            <Form.Item
              name="code"
              label="Reset code"
              rules={[{ required: true, message: 'Enter the code from your email' }]}
            >
              <Input placeholder="6-digit code" />
            </Form.Item>
            <Form.Item
              name="password"
              label="New password"
              rules={[
                { required: true, message: 'Enter a new password' },
                { min: 8, message: 'At least 8 characters' },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label="Confirm password"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Space style={{ width: '100%' }} direction="vertical">
              <Button type="primary" htmlType="submit" block loading={resetLoading}>
                Update password
              </Button>
              <Button type="link" onClick={() => setResetStep(0)} block>
                Use a different email
              </Button>
            </Space>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default LoginPageHook;
